import { DatePipe } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostBinding,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { AliasService } from 'src/app/authentication/alias/alias.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { AppAvatarComponent } from 'src/app/shared/avatar/avatar.component';
import { IconSizeDirective } from 'src/app/shared/directives/icon-size.directive';
import { ENV } from 'src/app/shared/env/environment.token';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { VERSION } from 'src/environments/version';
import { FeedbackService } from '../../core/feedback/feedback.service';
import { UserRolesComponent } from '../user-roles/user-roles.component';

@Component({
  selector: 'app-avatar-popover',
  templateUrl: './avatar-popover.component.html',
  styleUrl: './avatar-popover.component.scss',
  imports: [
    AppAvatarComponent,
    UserRolesComponent,
    MatIconModule,
    DatePipe,
    MatButtonModule,
    IconSizeDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AvatarPopoverComponent implements AfterViewChecked {
  readonly #authService = inject(AuthService);
  readonly #feedbackService = inject(FeedbackService);
  readonly #navigationService = inject(NavigationService);
  readonly #aliasService = inject(AliasService);
  readonly #env = inject(ENV);
  readonly #hostRef = inject<ElementRef<HTMLElement>>(ElementRef);

  readonly appVersion = signal(VERSION.version);
  readonly buildTime = signal(VERSION.buildTime);
  readonly aliasFeatureEnabled = signal(this.#env.trimAlias);
  readonly aliasTitle = computed(() => this.#aliasService.alias() || 'Alias');
  readonly user = toSignal(this.#authService.user$);

  @HostBinding('class.avatar-popover--scrollable')
  hasVerticalScrollbar = false;

  ngAfterViewChecked(): void {
    const host = this.#hostRef.nativeElement;
    const hasOverflow = host.scrollHeight > host.clientHeight + 1;

    if (this.hasVerticalScrollbar !== hasOverflow) {
      this.hasVerticalScrollbar = hasOverflow;
    }
  }

  clearAlias(): void {
    this.#aliasService.setAlias(undefined, false);

    this.#feedbackService.showSuccessWithTitle({
      title: '🧹 Alias cleared',
      message: `You are back to ${this.user()?.email} user`,
    });

    this.#navigationService.navigateToDashboard(undefined);
  }

  navigateToAlias(): void {
    this.#navigationService.navigateToAlias();
  }

  logout(): void {
    this.#authService.logout();
  }
}
